import {
  Box,
  Button,
  Checkbox,
  createTheme,
  FormControl,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { ThemeProvider } from "@mui/material/styles"
import HelpIcon from "@mui/icons-material/Help";
import {
  Autocomplete,
  Chip,
  CircularProgress,
  Grid,
  TextField,
} from "@mui/material";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import FieldNames from "../../../../defaults/FieldNames.json";
import GridDefinitions from "../../../../defaults/GridDefinitions.json";
import AuditLogService from "../../../../services/AuditLogService";
import CancelService from "../../../../services/CancelService";
import ErrorService from "../../../../services/ErrorService";
import FeatureService from "../../../../services/FeatureService";
import { getSaveableDate } from "../../../../utils/DateHelper";
import BasicTextField from "../../../details/customPanels/BasicTextField";
import InFormGenericModal from "../../../InFormGenericModal";
import * as esriLang from "@arcgis/core/core/lang.js";

const BlueAlien = "#1976d2";
const WhiteAlien = "#ffffff";

export default function CancelEntryPopup({
  currentUserInfo,
  userRoleInfo,
  cancelEditorOpen,
  setCancelEditorOpen,
  saveCallback,
  closeCallback,
  feature,
  isBulkCancel,
  allBulkSelectedItems,
}) {
  const useStyles = makeStyles({
    underline: {
      "&&&:before": {
        borderBottom: "2px solid red",
      },
      "&&:after": {
        borderBottom: "2px solid red",
      },
    },
    condensedFormControl: {
      margin: "5px 15px 5px 15px",
      minWidth: 120,
      width: "90%",
    },
    formControl: {
      margin: "10px 15px 10px 15px",
      minWidth: 120,
      width: "90%",
    },
    formControlHeader: {
      margin: "0px 15px 0px 15px",
      minWidth: 120,
      width: "90%",
    },
    snackBarStyle: {
      top: "120px !important",
    },
  });

  const useErrorStyles = makeStyles({
    underline: {
      "&&&:before": {
        borderBottom: "2px solid red",
      },
      "&&:after": {
        borderBottom: "2px solid red",
      },
    },
  });

  const classes = useStyles();
  const errorClasses = useErrorStyles();

  const theme = createTheme({
    overrides: {
      palette: {
        red: {
          main: "#FF0000",
          light: "#FF0000",
          dark: "#FF0000",
          contrastText: "#242105",
        },
      },
      MuiPaper: {
        root: {
          backgroundColor: "green",
        },
      },
      MuiOutlinedInput: {
        root: {
          "& $notchedOutline": {
            //initial border
            borderColor: BlueAlien,
          },
          "&:hover $notchedOutline": {
            //border on hover
            borderColor: BlueAlien,
            border: "2px solid",
          },
          "&$focused $notchedOutline": {
            //border on focus
            borderColor: BlueAlien,
            border: "2px solid",
          },
        },
      },
    },
  });

  // State Variables

  const [fullCancelReasons, setFullCancelReasons] = useState([]);
  const [cancelReasonOptions, setCancelReasonOptions] = useState([]);
  const [cancelSubReasonOptions, setCancelSubReasonOptions] = useState([]);
  const hasCancelSubReason =
    cancelSubReasonOptions && cancelSubReasonOptions.length > 0;
  const [cancelReasonOpen, setCancelReasonOpen] = useState(false);
  const cancelReasonLoading =
    cancelReasonOpen && cancelReasonOptions.length === 0;
  const [cancelSubReasonOpen, setCancelSubReasonOpen] = useState(false);
  const cancelSubReasonLoading =
    cancelSubReasonOpen && cancelSubReasonOptions.length === 0;
  const [substituteSelectionModel, setSubstituteSelectionModel] = useState({ids: new Set(), type: 'include'});

  const [cancelReasonSelection, setCancelReasonSelection] = useState(null);
  const [cancelSubReasonSelection, setCancelSubReasonSelection] =
    useState(null);
  const [cancelReasoning, setCancelReasoning] = useState("");
  const [cancelHasSubstitute, setCancelHasSubstitute] = useState(false);
  const [substituteSearchId, setSubstituteSearchId] = useState(null);
  const [cancelSubstituteSelection, setCancelSubstituteSelection] =
    useState(null);
  const [substituteGridRows, setSubstituteGridRows] = useState([]);
  const [substituteGridCols, setSubstituteGridCols] = useState([]);
  const [isCancelAddLoading, setIsCancelAddLoading] = useState(false);
  const [isCancelUpdateLoading, setIsCancelUpdateLoading] = useState(false);
  const [isCancelQueryActive, setIsCancelQueryActive] = useState(false);
  const [isSubstituteGridLoading, setIsSubstituteGridLoading] = useState(false);

  const gridSelectionLength =
    allBulkSelectedItems && allBulkSelectedItems.length > 0
      ? allBulkSelectedItems.length
      : 0;

  const featureAttr = feature?.attributes;

  const substituteGridColDefinitions = [
    GridDefinitions.SubstituteSelectionGrid.entityID,
    GridDefinitions.SubstituteSelectionGrid.class,
    GridDefinitions.SubstituteSelectionGrid.name,
  ];

  // Hooks

  useEffect(() => {
    establishSubstituteGridColumns();

    CancelService.getCancelReasons()
      .then((response) => {
        setFullCancelReasons(response);
      })
      .catch((error) => {
        ErrorService.addErrorLogEntry(
          currentUserInfo[FieldNames.userFields.nifcUsername],
          error
        );
      });
  }, []);

  useEffect(() => {
    if (!fullCancelReasons || fullCancelReasons.length <= 0) {
      return;
    }

    let mainCancelReasons = fullCancelReasons.map(
      (cr) => cr[FieldNames.cancelFields.reason]
    );
    let distinctMainCancelReasons = [...new Set(mainCancelReasons)];
    setCancelReasonOptions(distinctMainCancelReasons);
  }, [fullCancelReasons]);

  useEffect(() => {
    if (!cancelReasonSelection) {
      return;
    }

    let subCancelReasons = fullCancelReasons
      .filter(
        (cr) =>
          cr[FieldNames.cancelFields.reason] === cancelReasonSelection &&
          cr[FieldNames.cancelFields.subReason]
      )
      .map((cr) => cr[FieldNames.cancelFields.subReason]);
    let distinctSubCancelReasons = [...new Set(subCancelReasons)];
    setCancelSubReasonSelection(null);
    setCancelSubReasonOptions(distinctSubCancelReasons);
  }, [cancelReasonSelection]);

  useEffect(() => {
    if ((isCancelAddLoading || isCancelUpdateLoading) && !isCancelQueryActive) {
      setIsCancelQueryActive(true);
    }

    if (!isCancelAddLoading && !isCancelUpdateLoading && isCancelQueryActive) {
      setIsCancelQueryActive(false);
    }
  }, [
    isCancelAddLoading,
    isCancelUpdateLoading,
    // intentionally excluding isCancelQueryActive
  ]);

  // Logic

  const closeCancelEditor = () => {
    setCancelEditorOpen(false);
    setCancelReasonSelection(null);
    setCancelSubReasonSelection(null);
    setCancelReasoning("");
    setCancelHasSubstitute(false);
    setCancelSubstituteSelection(null);
    if (closeCallback) {
      closeCallback();
    }
  };

  const establishSubstituteGridColumns = () => {
    let columns = [];
    substituteGridColDefinitions.forEach((colDef) => {
      columns.push({
        field: colDef.value,
        headerName: colDef.header,
        type: colDef.type,
        hideable: colDef.hideable,
        sortable: colDef.sortable,
        editable: colDef.editable,
        groupable: colDef.groupable,
        flex: colDef.flex,
        align: colDef.align,
        disableExport: colDef.disableExport,
        valueGetter: (value) => {
          return value; // Used for formatting string values I.E. currency formatting
        },
      });
    });

    setSubstituteGridCols(columns);
  };

  const searchForSubstitute = () => {
    if (!substituteSearchId) {
      return;
    }

    setIsSubstituteGridLoading(true);
    setSubstituteGridRows([]);
    let finalList = [];
    (async () => {
      //Estimated Treatment
      await FeatureService.getFeaturesByObjectIDs([substituteSearchId])
        .then((rows) => {
          if (rows && rows.length > 0) {
            var mappedRows = rows.map((row) => {
              return {
                OBJECTID: row[FieldNames.featureFields.objectID],
                EntityID: row[FieldNames.featureFields.objectID],
                ID: row[FieldNames.featureFields.id],
                JoinID: row[FieldNames.featureFields.joinID],
                JoinFeature: row[FieldNames.featureFields.joinFeature],
                EntityType: FieldNames.tableNames.treatment,
                Class: FieldNames.classTypes.treatment,
                Name: row[FieldNames.featureFields.name],
              };
            });
            finalList = mappedRows;
          }
        })
        .catch((error) => {
          ErrorService.addErrorLogEntry(
            userRoleInfo[FieldNames.userFields.nifcUsername],
            error
          );
        });

      setSubstituteGridRows(finalList);
      setIsSubstituteGridLoading(false);
    })();
  };

  const confirmCancel = async () => {
    let featureToReturn = featureAttr;
    try {
      if (!featureAttr || (cancelHasSubstitute && !cancelSubstituteSelection)) {
        return;
      }
      let cancelObject = {
        JoinID: featureAttr[FieldNames.featureFields.joinID],
        JoinIDS: cancelSubstituteSelection?.[FieldNames.featureFields.joinID],
        JoinFeature: featureAttr[FieldNames.featureFields.joinFeature],
        JoinFeatureS:
          cancelSubstituteSelection?.[FieldNames.featureFields.joinFeature],
        EntityID: featureAttr[FieldNames.featureFields.entityID],
        EntityType: featureAttr[FieldNames.featureFields.entityType],
        CancelReason: cancelReasonSelection,
        CancelSubReason: cancelSubReasonSelection
          ? cancelSubReasonSelection
          : "",
        HasSubstitute: cancelHasSubstitute
          ? FieldNames.yesNo.yes
          : FieldNames.yesNo.no,
        SubstituteEntityType: cancelHasSubstitute
          ? cancelSubstituteSelection[FieldNames.featureFields.entityType]
          : "",
        SubstituteEntityID: cancelHasSubstitute
          ? cancelSubstituteSelection[FieldNames.featureFields.entityID]
          : "",
        ModifiedBy: currentUserInfo[FieldNames.userFields.nifcUsername],
        ModifiedDate: getSaveableDate(new Date()),
        IsArchived: FieldNames.yesNo.no,
      };

      if (
        featureAttr[FieldNames.featureFields.isCanceled] === FieldNames.yesNo.yes
      ) {
        // Feature has already been canceled before
        const cancelObj = await CancelService.getFeatureCancelReason(featureAttr[FieldNames.featureFields.joinFeature], featureAttr[FieldNames.featureFields.joinID]);
        cancelObject[FieldNames.featureFields.objectID] =
        cancelObj[FieldNames.featureFields.objectID];
      } else {
        cancelObject[FieldNames.featureFields.createdBy] =
          currentUserInfo[FieldNames.userFields.nifcUsername];
        cancelObject[FieldNames.featureFields.createdOnDate] = getSaveableDate(
          new Date()
        );
      }

      let featureAttrObj = {
        ...featureAttr,
        IsCanceled: FieldNames.yesNo.yes,
        Status: FieldNames.statuses.cancelled,
        CancelledDate: getSaveableDate(new Date()),
      };
      featureToReturn = esriLang.clone(feature);
      featureToReturn.attributes = featureAttrObj;

      let baseMessage =
        `Action was marked as canceled for the following reason: ${cancelReasonSelection}` +
        (cancelSubReasonSelection ? `: ${cancelSubReasonSelection}.` : `.`);
      let subBaseMessage = "";
      //   `Action was marked as a substitution for a canceled feature for the following reason: ${cancelReasonSelection}` +
      //   (cancelSubReasonSelection ? `: ${cancelSubReasonSelection}.` : `.`);
      let substituteFeatureObj = null;
      let subAuditLogMessage = "";

      if (cancelHasSubstitute) {
        let featureClass =
          cancelSubstituteSelection[FieldNames.featureFields.class];
        baseMessage =
          baseMessage +
          ` Action is substituted by ${featureClass} ${
            cancelSubstituteSelection[FieldNames.featureFields.objectID]
          }.`;

        subBaseMessage =
          subBaseMessage +
          ` Action is a substitute for ${
            featureAttr[FieldNames.featureFields.class]
          } ${featureAttr[FieldNames.featureFields.objectID]}.`;

        substituteFeatureObj = {
          OBJECTID:
            cancelSubstituteSelection[FieldNames.featureFields.objectID],
          IsSubstitute: FieldNames.yesNo.yes,
        };
        subAuditLogMessage = cancelReasoning
          ? subBaseMessage + " " + cancelReasoning
          : subBaseMessage;
      }
      let auditLogMessage = cancelReasoning
        ? baseMessage + " " + cancelReasoning
        : baseMessage;

      const cancelCheckClone = _.cloneDeep(
        featureAttr[FieldNames.featureFields.isCanceled]
      );
      const resp = await FeatureService.updateFeatureAttributes([featureAttrObj]);
      if (cancelCheckClone && cancelCheckClone === FieldNames.yesNo.yes) {
        // Update
        const cresp = await CancelService.updateCanceledFeatureEntry(
          cancelObject
        );
      } else {
        // New Cancel
        const cresp = await CancelService.addCanceledFeatureEntry(cancelObject);
      }
      const aresp = await AuditLogService.addAuditLogEntry(
        featureAttr,
        featureAttr[FieldNames.featureFields.joinID],
        featureAttr[FieldNames.featureFields.joinFeature],
        currentUserInfo[FieldNames.userFields.nifcUsername],
        auditLogMessage
      );
      if (substituteFeatureObj) {
        const substituteFeatureUpdated =
          await FeatureService.updateFeatureAttributes([substituteFeatureObj]);
        const auditResponse = await AuditLogService.addAuditLogEntry(
          cancelSubstituteSelection,
          cancelSubstituteSelection[FieldNames.featureFields.joinID],
          cancelSubstituteSelection[FieldNames.featureFields.joinFeature],
          currentUserInfo[FieldNames.userFields.nifcUsername],
          subAuditLogMessage
        );
      }
    } catch (e) {
      ErrorService.addErrorLogEntry(
        userRoleInfo[FieldNames.userFields.nifcUsername],
        e
      );
    } finally {
      setCancelEditorOpen(false);
      setCancelReasonSelection(null);
      setCancelSubReasonSelection(null);
      setCancelReasoning("");
      setCancelHasSubstitute(false);
      setCancelSubstituteSelection(null);
      setCancelReasonOpen("");
      saveCallback(featureToReturn);
    }
  };

  const confirmBulkCancel = () => {
    if (
      !isBulkCancel ||
      !allBulkSelectedItems ||
      allBulkSelectedItems.length === 0
    ) {
      return;
    }

    setIsCancelAddLoading(true);
    setIsCancelUpdateLoading(true);

    let baseMessage =
      `Action was marked as canceled for the following reason: ${cancelReasonSelection}` +
      (cancelSubReasonSelection ? `: ${cancelSubReasonSelection}.` : `.`);
    if (cancelHasSubstitute) {
      let featureClass =
        cancelSubstituteSelection[FieldNames.featureFields.class];
      baseMessage =
        baseMessage +
        ` Action is substituted by ${featureClass} ${
          cancelSubstituteSelection[FieldNames.featureFields.objectID]
        }.`;
    }
    let auditLogMessage = cancelReasoning
      ? baseMessage + " " + cancelReasoning
      : baseMessage;

    let featuresToUpdate = [];
    let cancelRowsToUpdate = [];
    let featuresToUpdateNewCancel = [];
    let cancelRowsToAdd = [];

    // TODO: bulk cancel is not adding audit logs appropriately

    allBulkSelectedItems.forEach((cancelRow) => {
      let isAlreadyCanceled = cancelRow[FieldNames.featureFields.isCanceled] && (cancelRow[FieldNames.cancelFields.constructedCancelObjectID] ?? null);

      let cancelObject = {
        JoinID: cancelRow[FieldNames.featureFields.joinID],
        JoinIDS: null, // No substitutes for bulk
        JoinFeature: cancelRow[FieldNames.featureFields.joinFeature],
        JoinFeatureS: "", // No substitutes for bulk 
        EntityOBJECTID: cancelRow[FieldNames.featureFields.objectID],
        EntityID: cancelRow[FieldNames.featureFields.entityID],
        EntityType: cancelRow[FieldNames.featureFields.entityType],
        CancelReason: cancelReasonSelection,
        CancelSubReason: cancelSubReasonSelection ?? "",
        HasSubstitute: FieldNames.yesNo.no,
        SubstituteEntityType: null, // No substitutes for bulk
        SubstituteEntityID: null, // No substitutes for bulk
        ModifiedBy: currentUserInfo[FieldNames.userFields.nifcUsername],
        ModifiedDate: getSaveableDate(new Date()),
        IsArchived: FieldNames.yesNo.no,
      };

      if (isAlreadyCanceled) {
        // Feature has already been canceled before
        cancelObject[FieldNames.featureFields.objectID] =
          cancelRow[FieldNames.cancelFields.constructedCancelObjectID];
      } else {
        cancelObject[FieldNames.featureFields.createdBy] =
          currentUserInfo[FieldNames.userFields.nifcUsername];
        cancelObject[FieldNames.featureFields.createdOnDate] = getSaveableDate(
          new Date()
        );
      }

      let featureObj = {
        OBJECTID: cancelRow[FieldNames.featureFields.objectID],
        ID: cancelRow[FieldNames.featureFields.id],
        IsCanceled: FieldNames.yesNo.yes,
        Status: FieldNames.statuses.cancelled,
        CancelledDate: getSaveableDate(new Date()),
      };

      if (isAlreadyCanceled) {
        cancelRowsToUpdate.push(cancelObject);
        featuresToUpdate.push(featureObj);
      } else {
        featuresToUpdateNewCancel.push(featureObj);
        cancelRowsToAdd.push(cancelObject);
      }
    });

    if (featuresToUpdateNewCancel.length > 0) {
      bulkCancelProcess(
        featuresToUpdateNewCancel,
        cancelRowsToAdd,
        auditLogMessage,
        false
      );
    } else {
      setIsCancelAddLoading(false);
    }
    if (featuresToUpdate.length > 0) {
      bulkCancelProcess(
        featuresToUpdate,
        cancelRowsToUpdate,
        auditLogMessage,
        true
      );
    } else {
      setIsCancelUpdateLoading(false);
    }
  };

  const bulkCancelProcess = async (
    featureArr,
    cancelArr,
    auditLogMessage,
    isUpdate
  ) => {
    try {
      let resp = await FeatureService.updateMultipleFeaturesAttributes(
        featureArr
      );
      if (resp?.successful && resp?.successful?.length > 0) {
        let objIDArray = resp?.successful;
        const arrToCancel = cancelArr?.filter((feature) =>
          objIDArray?.includes(feature["EntityOBJECTID"])
        );
        let csresp = await CancelService.addUpdateMultipleCancelFeatureEntries(
          arrToCancel,
          isUpdate
        );
        const auditList = arrToCancel?.map((row) => {
          return {
            [FieldNames.auditLogFields.joinID]:
              row[FieldNames.auditLogFields.joinID],
            [FieldNames.auditLogFields.joinFeature]:
              row[FieldNames.auditLogFields.joinFeature],
            [FieldNames.featureFields.entityType]:
              row[FieldNames.featureFields.entityType],
            [FieldNames.featureFields.entityID]:
              row[FieldNames.featureFields.entityID],
          };
        });

        const polyList = auditList.filter(
          (x) =>
            x[FieldNames.auditLogFields.joinFeature] ===
            FieldNames.auditLogJoinTypes.polygon
        );
        const lineList = auditList.filter(
          (x) =>
            x[FieldNames.auditLogFields.joinFeature] ===
            FieldNames.auditLogJoinTypes.line
        );
        const pointList = auditList.filter(
          (x) =>
            x[FieldNames.auditLogFields.joinFeature] ===
            FieldNames.auditLogJoinTypes.point
        );

        try {
          let polyResp = AuditLogService.addMultipleAuditLogs(
            FieldNames.auditLogJoinTypes.polygon,
            polyList,
            currentUserInfo[FieldNames.userFields.nifcUsername],
            auditLogMessage
          );

          let lineResp = AuditLogService.addMultipleAuditLogs(
            FieldNames.auditLogJoinTypes.line,
            lineList,
            currentUserInfo[FieldNames.userFields.nifcUsername],
            auditLogMessage
          );

          let pointResp = AuditLogService.addMultipleAuditLogs(
            FieldNames.auditLogJoinTypes.point,
            pointList,
            currentUserInfo[FieldNames.userFields.nifcUsername],
            auditLogMessage
          );

          let allResp = await Promise.all([polyResp, lineResp, pointResp]);
        } catch (e) {
          ErrorService.addErrorLogEntry(
            currentUserInfo[FieldNames.userFields.nifcUsername],
            e
          );
        }
      }
    } catch (e) {
      ErrorService.addErrorLogEntry(
        currentUserInfo[FieldNames.userFields.nifcUsername],
        e
      );
    } finally {
      saveCallback();
      setCancelReasoning("");
      setCancelReasonSelection("");
      setCancelSubReasonSelection("");
      if (isUpdate) {
        setIsCancelUpdateLoading(false);
      } else {
        setIsCancelAddLoading(false);
      }
    }
  };

  return (
    <InFormGenericModal
      openFlag={cancelEditorOpen}
      closeMethod={closeCancelEditor}
      titleText={"Cancel Action"}
      displayButton={false}
    >
      <Box
        style={{
          maxHeight: "300px",
          overflow: "auto",
          width: "100%",
          minWidth: "650px",
        }}
      >
        <FormControl variant="standard" className={classes.formControl}>
          <ThemeProvider theme={theme}>
            <Grid
              item
              size={12}
              container
              direction="row"
              className="grid-item"
              style={{ alignItems: "center" }}
            >
              {isBulkCancel ? (
                <>
                  <Grid item size={12}>
                    <Typography
                      display={"inline"}
                      variant="h6"
                      fontWeight={"fontWeightBold"}
                    >
                      You are about to cancel {gridSelectionLength} features.
                      Please review before proceeding.
                    </Typography>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item size={12}>
                    <Typography
                      display={"inline"}
                      variant="h5"
                      fontWeight={"fontWeightBold"}
                    >
                      Entity:{" "}
                    </Typography>
                    <Typography display={"inline"} variant="h5">
                      {feature ? feature[FieldNames.featureFields.name] : "err"}
                    </Typography>
                  </Grid>
                </>
              )}
              <Grid item size={12}>
                <Typography display={"inline"}>Cancel Reason: </Typography>
                <Typography display={"inline"} color={"red"}>
                  {" "}
                  *
                </Typography>
              </Grid>
              <Grid item size={6}>
                <Autocomplete
                  //multiple
                  disableClearable
                  id="cancelReasonSelection"
                  open={cancelReasonOpen}
                  value={cancelReasonSelection}
                  onChange={(event, newValue) => {
                    if (newValue === "N/A") {
                      newValue = "";
                    }
                    setCancelReasonSelection(newValue);
                  }}
                  onOpen={() => {
                    setCancelReasonOpen(true);
                  }}
                  onClose={() => {
                    setCancelReasonOpen(false);
                  }}
                  isOptionEqualToValue={(option, value) => {
                    return option === value || value === "";
                  }}
                  getOptionLabel={(option) => {
                    return option;
                  }}
                  options={cancelReasonOptions}
                  getOptionDisabled={(option) => {
                    if (cancelReasonSelection) {
                      const selected = cancelReasonSelection;
                      if (selected && option && selected === option) {
                        return true;
                      } else {
                        return false;
                      }
                    } else {
                      return false;
                    }
                  }}
                  loading={cancelReasonLoading}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                      <Chip
                        label={option}
                        {...getTagProps({ index })}
                        disabled={false}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                              {cancelReasonLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params?.InputProps?.endAdornment}
                            </React.Fragment>
                          ),
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item size={6} />
              {!hasCancelSubReason ? null : (
                <>
                  <Grid item size={12}>
                    <Typography display={"inline"}>
                      Cancel Sub-Reason:{" "}
                    </Typography>
                    <Typography display={"inline"} color={"red"}>
                      {" "}
                      *
                    </Typography>
                  </Grid>
                  <Grid item size={12}>
                    <Autocomplete
                      //multiple
                      disableClearable
                      id="cancelSubReasonSelection"
                      open={cancelSubReasonOpen}
                      value={cancelSubReasonSelection}
                      onChange={(event, newValue) => {
                        if (newValue === "N/A") {
                          newValue = "";
                        }
                        setCancelSubReasonSelection(newValue);
                      }}
                      onOpen={() => {
                        setCancelSubReasonOpen(true);
                      }}
                      onClose={() => {
                        setCancelSubReasonOpen(false);
                      }}
                      isOptionEqualToValue={(option, value) => {
                        return option === value || value === "";
                      }}
                      getOptionLabel={(option) => {
                        return option;
                      }}
                      options={cancelSubReasonOptions}
                      getOptionDisabled={(option) => {
                        if (cancelSubReasonSelection) {
                          const selected = cancelSubReasonSelection;
                          if (selected && option && selected === option) {
                            return true;
                          } else {
                            return false;
                          }
                        } else {
                          return false;
                        }
                      }}
                      loading={cancelSubReasonLoading}
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                          <Chip
                            label={option}
                            {...getTagProps({ index })}
                            disabled={false}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          slotProps={{
                            input: {
                              ...params.InputProps,
                              endAdornment: (
                                <React.Fragment>
                                  {params?.InputProps?.endAdornment}
                                </React.Fragment>
                              ),
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </>
              )}
              {isBulkCancel ? null : (
                <>
                  <Grid item size={5}>
                    <Typography display={"inline"}>
                      Substituted for another action?
                    </Typography>
                  </Grid>
                  <Grid item size={2}>
                    <Checkbox
                      checked={cancelHasSubstitute}
                      onChange={(event) => {
                        setCancelHasSubstitute(!cancelHasSubstitute);
                      }}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                    <Grid item size={5} />
                  </Grid>
                </>
              )}
              {!cancelHasSubstitute ? null : (
                <>
                  <Grid item size={12}>
                    <Typography display={"inline"}>Substitute ID:</Typography>
                  </Grid>
                  <Grid item size={6}>
                    <TextField
                      type="number"
                      variant="standard"
                      sx={{ minWidth: 240, whiteSpace: "pre-wrap" }}
                      onChange={(e) => {
                        setSubstituteSearchId(e.target.value);
                      }}
                      fullWidth={true}
                      value={substituteSearchId}
                    />
                  </Grid>
                  <Grid item size={6}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        searchForSubstitute();
                      }}
                      disabled={!substituteSearchId}
                      aria-label="edit"
                      sx={{ marginLeft: "5px" }}
                    >
                      Search
                    </Button>
                  </Grid>
                  <Grid item size={7}>
                    <Box
                      sx={{ backgroundColor: "#D3D3D3", marginTop: "10px" }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <HelpIcon
                        fontSize="small"
                        color="primary"
                        style={{ paddingRight: "5px" }}
                      />
                      <Typography fontSize={12}>
                        You must select an entity from the rows below
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item size={5} />
                  <Grid item size={12}>
                    <Box
                      sx={{
                        height: "250px",
                        width: "100%",
                        minWidth: "10px",
                      }}
                    >
                      <DataGridPremium
                        rows={substituteGridRows}
                        columns={substituteGridCols}
                        getRowId={(row) => {
                          return (
                            row[FieldNames.featureFields.entityType] +
                            "-" +
                            row[FieldNames.featureFields.entityID]
                          );
                        }}
                        loading={isSubstituteGridLoading}
                        checkboxSelection
                        rowSelectionModel={substituteSelectionModel}
                        onRowSelectionModelChange={(newSelection) => {
                          setSubstituteSelectionModel(newSelection);
                          let selectedItems = [];
                          let selectedGridValue = [];
                          newSelection.forEach((item) => {
                            selectedGridValue = substituteGridRows.filter(
                              (x) => {
                                return (
                                  x[FieldNames.featureFields.entityType] +
                                    "-" +
                                    x[FieldNames.featureFields.entityID] ===
                                  item
                                );
                              }
                            );
                            if (
                              selectedGridValue &&
                              selectedGridValue.length > 0
                            ) {
                              selectedItems.push(selectedGridValue[0]);
                            }
                          });
                          setCancelSubstituteSelection(selectedItems[0]);
                        }}
                      />
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
            <Grid
              item
              size={12}
              container
              direction="row"
              className="grid-item"
              style={{ alignItems: "center" }}
            >
              <Grid item size={12}>
                <BasicTextField
                  field="carryoverReason"
                  label=""
                  setValue={setCancelReasoning}
                  variant={"outlined"}
                  placeholder={"Enter notes for cancellation"}
                  useFullWidth={true}
                />
              </Grid>
            </Grid>
          </ThemeProvider>
        </FormControl>
        <Box sx={{ marginLeft: "60%", marginBottom: "5px" }}>
          <Button
            variant="outlined"
            onClick={() => {
              closeCallback();
              setCancelReasonSelection(null);
              setCancelSubReasonSelection(null);
              setCancelReasoning("");
              setCancelEditorOpen(false);
            }}
            aria-label="close"
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (isBulkCancel) {
                await confirmBulkCancel();
              } else {
                await confirmCancel();
              }
              setCancelEditorOpen(false);
            }}
            disabled={
              !cancelReasonSelection ||
              (hasCancelSubReason && !cancelSubReasonSelection) ||
              (cancelHasSubstitute && !cancelSubstituteSelection)
            }
            aria-label="edit"
            sx={{ marginLeft: "5px" }}
          >
            Save
          </Button>
        </Box>
      </Box>
    </InFormGenericModal>
  );
}
